import { Modal } from '@/components';
import { useCreateSite } from '@/remote/site-management/create-site';
import { useNextAvailableIdQuery } from '@/remote/site-management/fetch-next-availlable-id';
import type { CreateSiteData } from '@/types/site';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  BasicInfoTab,
  LocationInfoTab,
  NotificationSettingsTab,
  PipelineInfoTab,
  WaterInfoTab,
} from '../tabs';
import { siteValidationSchema } from './validation';

interface SiteAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type TabType =
  | 'basic'
  | 'location'
  | 'water'
  | 'facility'
  | 'pipeline'
  | 'investment'
  | 'notification';

const initialSiteData: CreateSiteData = {
  siteId: '',
  name: '',
  cdmaNumber: '',
  wellPosition: {
    latitude: 0,
    longitude: 0,
    address: '',
    level: 0,
    name: '',
  },
  motorActivateCount: 1,
  waterSupplyPosition: [],
  managementPosition: {
    latitude: 0,
    longitude: 0,
    address: '',
    level: 0,
    name: '',
  },
  managementType: '',
  facilityType: '',
  waterIntakeType: '',
  installedDate: '',
  motorSpecification: '',
  tankSize: 0,
  waterSupplyInfo: {
    plan: {
      family: 0,
      people: 0,
      waterSupplyPerPerson: 0,
    },
    current: {
      family: 0,
      people: 0,
      waterSupplyPerPerson: 0,
    },
    supplyLimit: '',
  },
  intakeFacilityInfo: [],
  purifyFacilityInfo: [],
  waterSupplyPipelineInfo: [],
  drainageSupplyPipelineInfo: [],
  facilityPlan: {
    repairDate: '',
    closeDate: '',
    supplyDate: '',
    distanceFromWaterSupply: 0,
    explanatoryNote: '',
  },
  alias: {
    circuitBreaker: '차단기',
    pressureSensor: '압력센서',
    external: '외부',
    preparation: '예비1',
    preparation2: '예비2',
  },
  waterSource: [],
  investmentInfos: [],
  lowWaterLevel: 40,
  highWaterLevel: 100,
  notificationManagerIds: [],
  electricWaveErrorSmsEnabled: false,
  overloadErrorSmsEnabled: false,
  undergroundWaterErrorSmsEnabled: false,
  waterLevelSmsEnabled: true,
};

export default function SiteAddModal({
  isOpen,
  onClose,
  onSuccess,
}: Readonly<SiteAddModalProps>) {
  const { data: nextAvailableId } = useNextAvailableIdQuery();
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const createSiteMutation = useCreateSite();

  const methods = useForm<CreateSiteData>({
    resolver: yupResolver<CreateSiteData, unknown, unknown>(
      siteValidationSchema
    ),
    defaultValues: initialSiteData,
    mode: 'all', // 모든 이벤트에서 validation (onChange, onBlur, onSubmit)
    reValidateMode: 'onChange', // 재검증도 onChange에서 실행
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = methods;

  console.log('errors', errors, 'isValid', isValid);

  // nextAvailableId가 로딩되면 siteId 설정
  useEffect(() => {
    if (nextAvailableId) {
      setValue('siteId', nextAvailableId.padStart(6, '0'), {
        shouldValidate: true,
        shouldDirty: false, // 초기값이므로 dirty로 표시하지 않음
        shouldTouch: false,
      });
    }
  }, [nextAvailableId, setValue]);

  // 현재 form 데이터 watch
  const siteData = watch();

  // 사이트 데이터 변경 핸들러 (기존 탭들을 위한 호환성 유지)
  const handleSiteChange = (updatedSite: CreateSiteData) => {
    Object.keys(updatedSite).forEach((key) => {
      setValue(
        key as keyof CreateSiteData,
        updatedSite[key as keyof CreateSiteData],
        {
          shouldValidate: true, // 값 변경 시 즉시 validation 트리거
          shouldDirty: true,
          shouldTouch: true,
        }
      );
    });
  };

  const handleClose = () => {
    reset(initialSiteData);
    setActiveTab('basic');
    onClose();
  };

  const onSubmit = async (data: CreateSiteData) => {
    try {
      await createSiteMutation.mutateAsync(data);

      // 성공 시 처리
      alert('현장이 성공적으로 추가되었습니다.');
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('현장 추가 실패:', error);
      alert('현장 추가 중 오류가 발생했습니다.');
    }
  };

  // 필수 필드 에러 확인
  const hasRequiredFieldErrors = () => {
    return !!(
      errors.siteId ||
      errors.name ||
      errors.cdmaNumber ||
      errors.wellPosition?.address ||
      errors.wellPosition?.name ||
      errors.wellPosition?.latitude ||
      errors.wellPosition?.longitude
    );
  };

  // 탭별 에러 표시를 위한 함수들
  const getTabErrorStatus = (tab: TabType): boolean => {
    switch (tab) {
      case 'basic':
        return !!(errors.siteId || errors.name || errors.cdmaNumber);
      case 'location':
        return !!(
          errors.wellPosition?.name ||
          errors.wellPosition?.address ||
          errors.wellPosition?.latitude ||
          errors.wellPosition?.longitude ||
          errors.waterSupplyPosition
        );
      case 'water':
        return !!errors.waterSupplyInfo;
      case 'facility':
        return !!(
          errors.intakeFacilityInfo ||
          errors.purifyFacilityInfo ||
          errors.facilityPlan
        );
      case 'pipeline':
        return !!(
          errors.waterSupplyPipelineInfo || errors.drainageSupplyPipelineInfo
        );
      case 'investment':
        return !!errors.investmentInfos;
      case 'notification':
        return !!(
          errors.lowWaterLevel ||
          errors.highWaterLevel ||
          errors.notificationManagerIds ||
          errors.electricWaveErrorSmsEnabled ||
          errors.overloadErrorSmsEnabled ||
          errors.undergroundWaterErrorSmsEnabled
        );
      default:
        return false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="새 현장 추가"
      size="4xl"
      maxHeight="max-h-[95vh]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {/* 탭 네비게이션 */}
          <div className="flex flex-wrap border-b border-gray-200 mb-6">
            {[
              { id: 'basic', label: '기본정보', required: true },
              { id: 'location', label: '위치정보', required: true },
              { id: 'water', label: '급수정보' },
              // { id: 'facility', label: '시설정보' },
              { id: 'pipeline', label: '배관정보' },
              // { id: 'investment', label: '투자정보' },
              { id: 'notification', label: '알림설정' },
            ].map((tab) => {
              const hasError = getTabErrorStatus(tab.id as TabType);
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg relative flex items-center gap-1 ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  <p className="text-red-500">{tab.required && '(필수)'}</p>
                  {/* 에러 표시 */}
                  {hasError && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 탭 컨텐츠 */}
          <div className="min-h-[400px]">
            {/* 기본정보 탭 - react-hook-form 사용 */}
            {activeTab === 'basic' && <BasicInfoTab />}

            {/* 위치정보 탭 - 기존 방식 유지 */}
            {activeTab === 'location' && (
              <LocationInfoTab
                site={siteData}
                onSiteChange={handleSiteChange}
              />
            )}

            {/* 급수정보 탭 - 기존 방식 유지 */}
            {activeTab === 'water' && (
              <WaterInfoTab site={siteData} onSiteChange={handleSiteChange} />
            )}

            {/* 시설정보 탭 - 기존 방식 유지 */}
            {/* {activeTab === 'facility' && (
              <FacilityInfoTab
                site={siteData}
                onSiteChange={handleSiteChange}
              />
            )} */}

            {/* 배관정보 탭 - 기존 방식 유지 */}
            {activeTab === 'pipeline' && (
              <PipelineInfoTab
                site={siteData}
                onSiteChange={handleSiteChange}
              />
            )}

            {/* 투자정보 탭 - 기존 방식 유지 */}
            {/* {activeTab === 'investment' && (
              <InvestmentInfoTab
                site={siteData}
                onSiteChange={handleSiteChange}
              />
            )} */}

            {/* 알림설정 탭 */}
            {activeTab === 'notification' && <NotificationSettingsTab />}
          </div>

          {/* 에러 메시지 표시 */}
          {hasRequiredFieldErrors() && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                필수 항목을 모두 입력해주세요:
              </p>
              <ul className="mt-1 text-xs text-red-500 list-disc list-inside">
                {errors.siteId && <li>{errors.siteId.message}</li>}
                {errors.name && <li>{errors.name.message}</li>}
                {errors.cdmaNumber && <li>{errors.cdmaNumber.message}</li>}
                {errors.wellPosition?.name && (
                  <li>관정명: {errors.wellPosition.name.message}</li>
                )}
                {errors.wellPosition?.address && (
                  <li>관정 주소: {errors.wellPosition.address.message}</li>
                )}
                {errors.wellPosition?.latitude && (
                  <li>관정 위도: {errors.wellPosition.latitude.message}</li>
                )}
                {errors.wellPosition?.longitude && (
                  <li>관정 경도: {errors.wellPosition.longitude.message}</li>
                )}
              </ul>
            </div>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={`px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isValid}
              className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center ${
                isSubmitting || !isValid ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  추가 중...
                </>
              ) : (
                '현장 추가'
              )}
            </button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
